import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { QuoteNoteApiService } from '../../services/quote-note-api.service';
import {
  QuoteNoteAttachment,
  QuoteNoteRequest,
  QuoteNoteResponse,
  parseNoteAttachments
} from '../../models/quote-note.model';
import { FileApiService } from '../../../correos/services/file-api.service';
import { UserApiService } from '../../../users/services/user-api.service';
import { CrmUserResponse } from '../../../users/models/user.model';
import { AuthService } from '../../../auth-pages/services/auth.service';
import { DatetimeService } from '../../../../shared/services/datetime.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { ConfirmDialogService } from '../../../../shared/services/confirm-dialog.service';
import { QuoteAttachmentIconComponent } from '../quote-attachments/quote-attachment-icon.component';

interface MentionChip {
  id: number;
  name: string;
}

/**
 * Notas internas de una cotización (incidente #5): listar, crear con @menciones
 * (autocompletado de usuarios del negocio) y adjuntos, editar/eliminar (solo autor).
 * Las menciones disparan notificación in-app desde el backend.
 */
@Component({
  selector: 'app-quote-notes',
  standalone: true,
  imports: [CommonModule, FormsModule, QuoteAttachmentIconComponent],
  templateUrl: './quote-notes.component.html'
})
export class QuoteNotesComponent implements OnChanges {
  @Input() quoteId: number | null = null;

  @ViewChild('composerTextarea') composerTextarea?: ElementRef<HTMLTextAreaElement>;

  /** Tipos aceptados al adjuntar (PDF + imágenes). */
  readonly accept = 'application/pdf,image/png,image/jpeg,image/webp,image/gif';
  private readonly MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB
  private readonly MAX_CONTENT = 5000;

  notes: QuoteNoteResponse[] = [];
  loading = false;
  loadError = '';

  // ---- Composer (creación o edición; solo uno activo a la vez) ----
  editingNoteId: number | null = null;
  draftContent = '';
  draftMentions: MentionChip[] = [];
  draftAttachments: QuoteNoteAttachment[] = [];
  saving = false;

  // Subida de adjuntos
  uploadingFile = false;
  uploadingFileName = '';

  // Eliminación en curso
  deletingIds = new Set<number>();

  // ---- Autocompletado de @menciones ----
  private allUsers: CrmUserResponse[] = [];
  showMentionDropdown = false;
  mentionResults: CrmUserResponse[] = [];
  mentionActiveIndex = 0;
  private mentionQuery = '';
  private mentionAnchor = 0; // índice del '@' en draftContent

  constructor(
    private noteApi: QuoteNoteApiService,
    private fileApi: FileApiService,
    private userApi: UserApiService,
    private authService: AuthService,
    private datetime: DatetimeService,
    private toast: ToastService,
    private confirmDialog: ConfirmDialogService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['quoteId']) {
      this.resetComposer();
      this.notes = [];
      if (this.quoteId) {
        this.cargar();
        this.cargarUsuarios();
      }
    }
  }

  private get businessId(): number {
    return this.authService.getCurrentUser()?.businessId || 1;
  }

  private get currentUserId(): number | null {
    return this.authService.getCurrentUser()?.id ?? null;
  }

  // ---- Carga ----

  private cargar(): void {
    if (!this.quoteId) return;
    this.loading = true;
    this.loadError = '';
    this.noteApi.getNotes(this.quoteId).subscribe({
      next: (notes) => {
        this.notes = notes || [];
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.loadError = err.error?.message || 'No se pudieron cargar las notas.';
      }
    });
  }

  // Usuarios del negocio para las menciones. El endpoint NO busca por texto → se
  // cargan todos los activos una vez y se filtran client-side. Error → sin menciones.
  private cargarUsuarios(): void {
    this.userApi.listActiveByBusiness(this.businessId).subscribe({
      next: (users) => {
        this.allUsers = (users || []).filter(u => u.id !== this.currentUserId);
      },
      error: () => {
        this.allUsers = [];
      }
    });
  }

  // ---- Composer ----

  get isEditing(): boolean {
    return this.editingNoteId !== null;
  }

  get contentLength(): number {
    return this.draftContent.length;
  }

  startEdit(note: QuoteNoteResponse): void {
    this.editingNoteId = note.id;
    this.draftContent = note.content;
    this.draftMentions = (note.mentionedUsers || []).map(u => ({ id: u.id, name: u.name }));
    this.draftAttachments = parseNoteAttachments(note.attachments);
    this.showMentionDropdown = false;
  }

  cancelEdit(): void {
    this.resetComposer();
  }

  private resetComposer(): void {
    this.editingNoteId = null;
    this.draftContent = '';
    this.draftMentions = [];
    this.draftAttachments = [];
    this.showMentionDropdown = false;
    this.uploadingFile = false;
    this.uploadingFileName = '';
  }

  removeMention(id: number): void {
    this.draftMentions = this.draftMentions.filter(m => m.id !== id);
  }

  removeAttachment(index: number): void {
    this.draftAttachments = this.draftAttachments.filter((_, i) => i !== index);
  }

  submit(): void {
    if (this.saving || this.uploadingFile) return;
    const content = this.draftContent.trim();
    if (!content) {
      this.toast.error({ title: 'Nota vacía', message: 'Escribe el contenido de la nota.' });
      return;
    }
    if (content.length > this.MAX_CONTENT) {
      this.toast.error({ title: 'Nota muy larga', message: `Máximo ${this.MAX_CONTENT} caracteres.` });
      return;
    }
    if (!this.quoteId) return;

    // Solo se envían los ids de las menciones cuyo "@Nombre" sigue presente en el texto.
    const activeMentions = this.draftMentions.filter(m => content.includes(`@${m.name}`));
    const request: QuoteNoteRequest = {
      content,
      mentions: JSON.stringify(activeMentions.map(m => m.id)),
      attachments: JSON.stringify(this.draftAttachments)
    };

    this.saving = true;
    const editingId = this.editingNoteId;
    const obs = editingId !== null
      ? this.noteApi.updateNote(this.quoteId, editingId, request)
      : this.noteApi.createNote(this.quoteId, request);

    obs.subscribe({
      next: (saved) => {
        this.saving = false;
        if (editingId !== null) {
          this.notes = this.notes.map(n => (n.id === editingId ? saved : n));
          this.toast.updated('Nota');
        } else {
          this.notes = [...this.notes, saved];
          this.toast.created('Nota');
        }
        this.resetComposer();
      },
      error: (err) => {
        this.saving = false;
        const msg = err?.status === 403
          ? 'Solo el autor puede editar esta nota.'
          : err.error?.message || 'No se pudo guardar la nota.';
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  async eliminar(note: QuoteNoteResponse): Promise<void> {
    if (!this.quoteId || this.deletingIds.has(note.id)) return;
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar nota',
      message: '¿Eliminar esta nota? Esta acción no se puede deshacer.'
    });
    if (!ok) return;

    this.deletingIds.add(note.id);
    this.noteApi.deleteNote(this.quoteId, note.id).subscribe({
      next: () => {
        this.deletingIds.delete(note.id);
        this.notes = this.notes.filter(n => n.id !== note.id);
        if (this.editingNoteId === note.id) this.resetComposer();
        this.toast.deleted('Nota');
      },
      error: (err) => {
        this.deletingIds.delete(note.id);
        const msg = err?.status === 403
          ? 'Solo el autor puede eliminar esta nota.'
          : err.error?.message || 'No se pudo eliminar la nota.';
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  // ---- Adjuntos ----

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    input.value = '';
    if (!file) return;

    const okType = file.type === 'application/pdf' || file.type.startsWith('image/');
    if (!okType) {
      this.toast.error({ title: 'Tipo no permitido', message: 'Solo se permiten PDF o imágenes.' });
      return;
    }
    if (file.size > this.MAX_FILE_BYTES) {
      this.toast.error({ title: 'Archivo muy grande', message: 'El archivo no debe superar 10 MB.' });
      return;
    }

    this.uploadingFile = true;
    this.uploadingFileName = file.name;
    this.fileApi.uploadFile(file).subscribe({
      next: (res) => {
        this.uploadingFile = false;
        this.uploadingFileName = '';
        this.draftAttachments = [
          ...this.draftAttachments,
          {
            storageRef: String(res.fileId),
            fileName: res.originalName || res.fileName,
            mimeType: res.contentType,
            sizeBytes: res.fileSize,
            url: res.publicUrl || ''
          }
        ];
      },
      error: (err) => {
        this.uploadingFile = false;
        this.uploadingFileName = '';
        this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo subir el archivo.' });
      }
    });
  }

  // ---- Autocompletado de @menciones ----

  onComposerInput(): void {
    const ta = this.composerTextarea?.nativeElement;
    if (!ta) return;
    this.draftContent = ta.value;
    const caret = ta.selectionStart ?? ta.value.length;
    const upto = ta.value.slice(0, caret);
    const match = /(?:^|\s)@([\p{L}\p{N}._-]*)$/u.exec(upto);
    if (match) {
      this.mentionQuery = match[1].toLowerCase();
      this.mentionAnchor = caret - match[1].length - 1;
      this.refreshMentionResults();
      this.showMentionDropdown = this.mentionResults.length > 0;
      this.mentionActiveIndex = 0;
    } else {
      this.showMentionDropdown = false;
    }
  }

  private refreshMentionResults(): void {
    const q = this.mentionQuery;
    const matches = this.allUsers.filter(u => {
      if (!q) return true;
      return this.userName(u).toLowerCase().includes(q) || (u.mail || '').toLowerCase().includes(q);
    });
    this.mentionResults = matches.slice(0, 8);
  }

  onComposerKeydown(event: KeyboardEvent): void {
    if (!this.showMentionDropdown || this.mentionResults.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.mentionActiveIndex = (this.mentionActiveIndex + 1) % this.mentionResults.length;
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.mentionActiveIndex = (this.mentionActiveIndex - 1 + this.mentionResults.length) % this.mentionResults.length;
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.selectMention(this.mentionResults[this.mentionActiveIndex]);
    } else if (event.key === 'Escape') {
      this.showMentionDropdown = false;
    }
  }

  onComposerBlur(): void {
    // Retraso para permitir el click en una opción del dropdown antes de cerrarlo.
    setTimeout(() => (this.showMentionDropdown = false), 150);
  }

  selectMention(user: CrmUserResponse): void {
    const ta = this.composerTextarea?.nativeElement;
    const name = this.userName(user);
    const caret = ta?.selectionStart ?? this.draftContent.length;
    const before = this.draftContent.slice(0, this.mentionAnchor);
    const after = this.draftContent.slice(caret);
    const inserted = `@${name} `;
    this.draftContent = before + inserted + after;
    if (!this.draftMentions.some(m => m.id === user.id)) {
      this.draftMentions = [...this.draftMentions, { id: user.id, name }];
    }
    this.showMentionDropdown = false;
    const pos = (before + inserted).length;
    setTimeout(() => {
      if (ta) {
        ta.focus();
        ta.setSelectionRange(pos, pos);
      }
    });
  }

  userName(user: CrmUserResponse): string {
    return `${user.fullname || ''}${user.lastname ? ' ' + user.lastname : ''}`.trim() || user.mail;
  }

  // ---- Presentación ----

  isAuthor(note: QuoteNoteResponse): boolean {
    return this.currentUserId !== null && note.userId === this.currentUserId;
  }

  isDeleting(note: QuoteNoteResponse): boolean {
    return this.deletingIds.has(note.id);
  }

  noteAttachments(note: QuoteNoteResponse): QuoteNoteAttachment[] {
    return parseNoteAttachments(note.attachments);
  }

  /** HTML del contenido con las @menciones resaltadas (escapado para evitar inyección). */
  renderedContent(note: QuoteNoteResponse): string {
    let html = this.escapeHtml(note.content);
    const users = [...(note.mentionedUsers || [])].sort((a, b) => b.name.length - a.name.length);
    for (const u of users) {
      const token = this.escapeHtml(`@${u.name}`);
      if (!token) continue;
      html = html.split(token).join(
        `<span class="font-semibold text-brand-600 dark:text-brand-400">${token}</span>`
      );
    }
    return html;
  }

  private escapeHtml(text: string): string {
    return (text || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  authorInitials(note: QuoteNoteResponse): string {
    const parts = (note.authorName || '?').trim().split(/\s+/);
    const first = parts[0]?.charAt(0) || '';
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
    return (first + last).toUpperCase() || '?';
  }

  relativeTime(iso: string): string {
    const time = new Date(iso).getTime();
    if (isNaN(time)) return '';
    const diffMs = Date.now() - time;
    if (diffMs < 0) return this.datetime.formatDateTimeShort(iso);
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'hace un momento';
    if (mins < 60) return `hace ${mins} min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `hace ${hours} h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `hace ${days} d`;
    return this.datetime.formatDateShort(iso);
  }

  formatDateTime(iso?: string | null): string {
    return iso ? this.datetime.formatDateTimeShort(iso) : '';
  }

  formatSize(bytes?: number | null): string {
    if (!bytes || bytes <= 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
